import { safeGet } from '../../../../core/utils/safe-get.js';
import { AxisIdToScales } from '../../../context/XYChartScales.context.js';
import { XYChartSelectionRectangleMetadata } from '../../../hooks/useSortRectangleDatapoints.js';
import {
  BAR_ID,
  RECT_ID,
  RECT_VARIANT,
  type AxisIdToScaleDomain,
  type BarMetadata,
  type BarSeriesWithDatapoints,
  type MapDatapoints,
  type RectMetadata,
  type RectSeriesWithDatapoints,
} from '../../../types/xy-chart-internals.js';
import {
  getCurrentScale,
  getDimensionScale,
} from '../../../utils/get-scale.js';
import { isRectDatapoint } from '../../../utils/type-guards.js';
import { relAccessor } from '../../../utils/value-representation.js';
import {
  heightAccessor as barHeightAccessor,
  widthAccessor as barWidthAccessor,
  xAccessor as barXAccessor,
  yAccessor as barYAccessor,
} from '../../displays/bar-accessors.js';
import {
  isXBaselineAccessor,
  isYBaselineAccessor,
} from '../../displays/BarDisplay.js';
import {
  heightAccessor as rectHeightAccessor,
  widthAccessor as rectWidthAccessor,
  xAccessor as rectXAccessor,
  yAccessor as rectYAccessor,
} from '../../displays/rect-accessors.js';
import type { RectanglePoint } from '../explore/types.js';

export const serializeRectangleSeriesValues = (
  axisIdToScales: AxisIdToScales,
  rectMetadata: Map<RectSeriesWithDatapoints, RectMetadata>,
  axisIdToScaleDomain: AxisIdToScaleDomain,
  datapointMetadataMap: MapDatapoints,
  barMetadata?: Map<BarSeriesWithDatapoints, BarMetadata>,
) => {
  return (point: XYChartSelectionRectangleMetadata): RectanglePoint => {
    if (
      isRectDatapoint(point.datapoint) &&
      point.series.type === RECT_VARIANT
    ) {
      const currentXScale = getCurrentScale(
        axisIdToScales,
        point.series.xAxisId,
      );
      const currentYScale = getCurrentScale(
        axisIdToScales,
        point.series.yAxisId,
      );

      const { xAxis, yAxis } = safeGet(rectMetadata, point.series);
      const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
      const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

      return {
        ...point,
        id: RECT_ID,
        x: rectXAccessor(currentXScale, xRelAccessor)(point.datapoint),
        y: rectYAccessor(currentYScale, yRelAccessor)(point.datapoint),
        width: rectWidthAccessor(currentXScale, xRelAccessor)(point.datapoint),
        height: rectHeightAccessor(
          currentYScale,
          yRelAccessor,
        )(point.datapoint),
      };
    }

    const xScale = getCurrentScale(
      axisIdToScales,
      point.series.xAxisId,
      isXBaselineAccessor(point.series.data[0]) ? point.series.type : undefined,
    );
    const dimensionXScale = getDimensionScale(
      axisIdToScales,
      point.series.xAxisId,
    );
    const yScale = getCurrentScale(
      axisIdToScales,
      point.series.yAxisId,
      isYBaselineAccessor(point.series.data[0]) ? point.series.type : undefined,
    );
    const dimensionYScale = getDimensionScale(
      axisIdToScales,
      point.series.yAxisId,
    );

    const barMeta = barMetadata
      ? safeGet(barMetadata, point.series)
      : undefined;
    const xRelAccessor = barMeta
      ? relAccessor(barMeta.xAxis, axisIdToScaleDomain)
      : undefined;
    const yRelAccessor = barMeta
      ? relAccessor(barMeta.yAxis, axisIdToScaleDomain)
      : undefined;

    const x = barXAccessor(
      xScale,
      datapointMetadataMap,
      dimensionXScale,
      point.series.id,
      xRelAccessor,
    )(point.datapoint);
    const y = barYAccessor(
      yScale,
      datapointMetadataMap,
      dimensionYScale,
      point.series.id,
      yRelAccessor,
    )(point.datapoint);
    const width = barWidthAccessor(
      xScale,
      datapointMetadataMap,
      dimensionXScale,
      xRelAccessor,
    )(point.datapoint);
    const height = barHeightAccessor(
      yScale,
      datapointMetadataMap,
      dimensionYScale,
      yRelAccessor,
    )(point.datapoint);

    return {
      ...point,
      id: BAR_ID,
      x,
      y,
      width,
      height,
    };
  };
};
